import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  useEffect,
  useRef,
} from 'react'
import { tokenService } from '@/lib/token.service'
import type { User, UserRole } from '@/schemas/user.schema'
import { useQuery } from '@tanstack/react-query'
import { USER_QUERY_KEYS } from '@/api/queryKeys'
import { getUserProfile } from '@/api/userApi'

interface UserRoleContextType {
  currentUser: User | null
  currentRole: UserRole | null
  updateUserRole: (newRole: UserRole) => void
  updateUser: (updates: Partial<User>) => void
}

export const UserRoleContext = createContext<UserRoleContextType | undefined>(
  undefined
)

export function UserRoleProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [currentRole, setCurrentRole] = useState<UserRole | null>(null)
  const isInitiallyFetch = useRef(false)
  const user = tokenService.getUser()
  const enabled = user.id !== '' && !isInitiallyFetch.current

  const { data: userData } = useQuery({
    queryKey: USER_QUERY_KEYS.all,
    queryFn: getUserProfile,
    enabled,
  })

  useEffect(() => {
    const user = tokenService.getUser()
    setCurrentUser(user ?? null)
    setCurrentRole(user?.userRoles?.[0] ?? null)
  }, [])

  useEffect(() => {
    if (!userData || isInitiallyFetch.current) return
    const storedUser = tokenService.getUser()

    const hasSelectedRole =
      storedUser?.userRoles?.length === 1 && userData.userRoles?.length > 1

    if (hasSelectedRole) {
      const selectedRole = storedUser.userRoles[0]
      const isRoleStillValid = userData?.userRoles.some(
        (role: UserRole) => role.id === selectedRole.id
      )

      if (isRoleStillValid) {
        const updatedUser = {
          ...userData,
          userRoles: [selectedRole],
        }
        tokenService.saveUser(updatedUser)
        setCurrentUser(updatedUser)
        setCurrentRole(selectedRole)
      } else {
        tokenService.saveUser(userData)
        setCurrentUser(userData)
        setCurrentRole(userData?.userRoles?.[0] ?? null)
      }
    } else {
      tokenService.saveUser(userData)
      setCurrentUser(userData)
      setCurrentRole(prevRole => prevRole ?? userData?.userRoles?.[0] ?? null)
    }
    isInitiallyFetch.current = true
  }, [userData])

  const updateUserRole = useCallback((newRole: UserRole) => {
    const user = tokenService.getUser()
    const updatedUser = {
      ...user,
      userRoles: [newRole],
    }
    tokenService.saveUser(updatedUser)
    setCurrentUser(updatedUser)
    setCurrentRole(newRole)
  }, [])

  const updateUser = (updates: Partial<User>) => {
    if (currentUser) {
      const updatedUser = { ...currentUser, ...updates }
      setCurrentUser(updatedUser)
      tokenService.saveUser(updates)
    }
  }

  return (
    <UserRoleContext.Provider
      value={{ currentUser, currentRole, updateUserRole, updateUser }}
    >
      {children}
    </UserRoleContext.Provider>
  )
}

export function useUserRole() {
  const context = useContext(UserRoleContext)
  if (context === undefined) {
    throw new Error('useUserRole must be used within a UserRoleProvider')
  }
  return context
}
